import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import PageNavigator from '@/src/components/project/Inbox2/Elements/PageNavigator/PageNavigator';
import DefaultFileEntryViewer from '@/src/components/project/Inbox2/pages/InboxEntryViewerPage/FileEntryViewerPage/FileEntryViewer/FileEntryViewer';
import { EmailFileEntry, FileEntry } from '@/src/components/project/Inbox2/types';

import useEmailFileEntryViewerStore from '../../../../components/EntryPanel/EmailThreadEntryViewer/components/EmailFileEntryViewer/useEmailFileEntryViewerStore';
import { useMappeEmailEntryFile } from '../../../../components/EntryPanel/EmailThreadEntryViewer/components/EmailFileEntryViewer/useMappedEmailEntryFile';
import { FileEntryViewerInfoPanelProvider } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.infoPanel.store';
import { FileEntryViewerSidebarActionsProvider } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.sidebarActions.store';
import { FileEntryViewerProvider } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.store';
import { FileEntryViewerTreatmentProvider } from '../../../../components/EntryPanel/FileEntryViewer/store/fileEntryViewer.treatment.store';
import StatusBadge from '../../../../Elements/StatusBadge/StatusBadge';

type HeaderProps = {
  fileEntry: FileEntry;
  files: EmailFileEntry[];
  totalCount: number;
  onSetEmailFileEntry: (emailFileEntry: any) => void;
  onFetchNextPage: (index: number) => void;
  refetchFileEntry?: () => void;
};

const Header = ({
  fileEntry,
  files,
  totalCount,
  onSetEmailFileEntry,
  onFetchNextPage,
}: HeaderProps) => {
  const currentIndex = useMemo(() => {
    return files.findIndex((f) => f.file.id === fileEntry.id);
  }, [files, fileEntry.id]);

  const handleClickLeft = () => {
    onSetEmailFileEntry(files[currentIndex - 1]);
  };

  const handleClickRight = async () => {
    const nextIndex = currentIndex + 1;
    const nextFile = files[nextIndex];
    if (nextFile) {
      onSetEmailFileEntry(files[nextIndex]);
    } else {
      onFetchNextPage(nextIndex);
    }
  };

  return (
    <div
      className={clsx(
        'tw-flex tw-items-center tw-justify-between',
        'tw-shadow-none',
        'tw-p-[10px] tw-mb-[10px]'
      )}
    >
      <Button
        id={''}
        className={clsx(
          'tw-group',
          'tw-flex tw-items-center',
          'tw-text-cfo-black tw-font-brandonTextMedium',
          'hover:tw-text-cfo-blue-700'
        )}
        onClick={() => onSetEmailFileEntry(null)}
      >
        <Icon icon={ICONS.close} className={clsx('tw-mr-[5px] tw-text-[18px]')} />
        {fileEntry.file.name}
      </Button>
      <div className={'tw-flex tw-items-center'}>
        <StatusBadge status={fileEntry.status} isDuplicate={fileEntry.duplicate} />
        <PageNavigator
          currentPage={currentIndex + 1}
          totalCount={totalCount}
          onClickLeft={handleClickLeft}
          onClickRight={handleClickRight}
        />
      </div>
    </div>
  );
};

type FileViewerProps = {
  emailFileEntry: EmailFileEntry;
  allFiles: EmailFileEntry[];
  inboxEntryId: number;
  emailId: number;
  projectId: string;
  totalCount: number;
  onSetEmailFileEntry: (emailFileEntry: any) => void;
};

const FileEntryViewer = ({
  emailFileEntry,
  allFiles,
  inboxEntryId,
  emailId,
  projectId,
  totalCount,
  onSetEmailFileEntry,
}: FileViewerProps) => {
  const [nextIndex, setNextIndex] = useState(null);
  const { mappedFileEntry, isLoading } = useMappeEmailEntryFile({
    projectId,
    inboxEntryId,
    emailId,
    fileId: emailFileEntry.file.id,
  });
  const { sidebarActions, treatmentEvents, infoPanelQueries } = useEmailFileEntryViewerStore({
    projectId,
    emailId,
    inboxEntryId,
    fileId: mappedFileEntry.id,
    documentFileId: mappedFileEntry.file.id,
  });

  useEffect(() => {
    if (!allFiles[nextIndex]) {
      return;
    }

    onSetEmailFileEntry(allFiles[nextIndex]);
    setNextIndex(null);
  }, [nextIndex, allFiles, onSetEmailFileEntry]);

  const handleFetchNextPage = (index: number) => {
    // onFetchNextPage();
    setNextIndex(index);
  };

  if (isLoading || !mappedFileEntry) {
    return <Spinner />;
  }

  return (
    <FileEntryViewerTreatmentProvider treatmentEvents={treatmentEvents}>
      <FileEntryViewerInfoPanelProvider infoPanelQueries={infoPanelQueries}>
        <FileEntryViewerSidebarActionsProvider sidebarActions={sidebarActions}>
          <FileEntryViewerProvider>
            <DefaultFileEntryViewer
              fileEntry={mappedFileEntry}
              header={
                <Header
                  onSetEmailFileEntry={onSetEmailFileEntry}
                  fileEntry={mappedFileEntry}
                  files={allFiles}
                  totalCount={totalCount}
                  onFetchNextPage={handleFetchNextPage}
                  // refetchFileEntry={fileDetailsMutation.refetch}
                />
              }
              mainContainerClass={
                'tw-sticky tw-top-0 tw-left-0 tw-z-[10] !tw-h-auto !tw-p-0 !tw-w-full'
              }
              entryCardClass={clsx('tw-min-h-[calc(100vh-110px)]')}
              isContainerFullScreen={true}
              isViewerFullScreen={false}
              settings={{
                isTransactionsEnabled: true,
              }}
            />
          </FileEntryViewerProvider>
        </FileEntryViewerSidebarActionsProvider>
      </FileEntryViewerInfoPanelProvider>
    </FileEntryViewerTreatmentProvider>
  );
};

export default FileEntryViewer;
